import React from 'react'

import { Button } from '../../../main/components/button'
import { Delayed } from '../../../main/components/delayed'
import { getExtendedDialogState } from '../../../main/components/dialog'
import { Icon } from '../../../main/components/icon'
import { Popover, PopoverStateReturn, PopoverTrigger, usePopoverState } from '../../../main/components/popover'
import { PopoverPlacement } from '../../../main/components/popover/types'
import { Spinner } from '../../../main/components/spinner'
import { useAsyncModule, withSuspense } from '../../../main/services/async-modules-utils'
import {
  chartSettingsAsyncModule,
  preventClosingUnsavedChangesAsyncModule,
  useChartLayoutGlobalModelAsync,
} from '../../charts-lib'
import { useOnSettingsClick } from '../hooks/use-on-settings-click'

const BUTTON_POPOVER_GUTTER = 16
const SETTINGS_POPOVER_ID = 'settings-popover'

type PopoverStylesType = { body?: React.CSSProperties; wrapper?: React.CSSProperties }

export function ScrollableChartSettingsPopover({ state }: { state: PopoverStateReturn; page: string }) {
  const [chartSettingsModule] = useAsyncModule({
    ...chartSettingsAsyncModule,
    shouldLoadModule: state.visible,
    errorSeverity: 'medium',
    onError: () => state.hide(),
  })
  const [preventClosingUnsavedChangesModule] = useAsyncModule({
    ...preventClosingUnsavedChangesAsyncModule,
    shouldLoadModule: state.visible,
    errorSeverity: 'medium',
    onError: () => state.hide(),
  })

  /**
   * The reason for this workaround is that the popover with a loader while fetching async modules remounts and resets
   * all animations when all async modules are loaded and the popover is rerendered inside PreventClosingUnsavedChanges
   * */
  const checkForUnsavedChangesAndCloseRef = React.useRef(() => {})

  const { ChartSettings } = chartSettingsModule ?? {}
  const { PreventClosingUnsavedChanges } = preventClosingUnsavedChangesModule ?? {}
  const { isFullyClosed } = getExtendedDialogState(state)
  const chartElement = document.getElementById('chart')
  const popoverContentElement = document.getElementById(SETTINGS_POPOVER_ID)
  const settingsButton = state.unstable_referenceRef.current

  const popoverStyles = React.useMemo<PopoverStylesType>(() => {
    let newPopoverStyles: PopoverStylesType = {}
    if (!settingsButton || !chartElement) {
      return newPopoverStyles
    }
    const settingsButtonBoundingClientRect = settingsButton.getBoundingClientRect()
    const topOffset = settingsButtonBoundingClientRect.top + settingsButtonBoundingClientRect.height
    newPopoverStyles.body = {
      maxHeight: window.innerHeight - topOffset - BUTTON_POPOVER_GUTTER * 2,
      position: 'sticky',
      top: -topOffset + BUTTON_POPOVER_GUTTER - window.scrollY,
    }
    if (!popoverContentElement) {
      return newPopoverStyles
    }
    const chartHeight = chartElement.clientHeight
    const popoverContentHeight = popoverContentElement.offsetHeight
    const isRedesign = FinvizSettings.hasRedesignEnabled
    newPopoverStyles.wrapper = {
      height: popoverContentHeight < chartHeight ? chartHeight : undefined,
      ...(!isRedesign && {
        transform: `translate(${BUTTON_POPOVER_GUTTER}px, ${topOffset + BUTTON_POPOVER_GUTTER + window.scrollY}px)`,
      }),
    }

    return newPopoverStyles
  }, [settingsButton, chartElement, popoverContentElement])

  return (
    <Popover
      id={SETTINGS_POPOVER_ID}
      aria-label="Chart Settings"
      className="relative z-10 flex max-h-full w-145 max-w-full flex-col"
      state={state}
      style={popoverStyles.body}
      popoverWrapperStyle={popoverStyles.wrapper}
      focusOnHide={false}
      hideOnClickOutside={false}
      onOverlayClick={checkForUnsavedChangesAndCloseRef.current}
    >
      {!!(PreventClosingUnsavedChanges && ChartSettings) ? (
        <PreventClosingUnsavedChanges onHide={state.hide}>
          {({ checkForUnsavedChangesAndClose }) => {
            checkForUnsavedChangesAndCloseRef.current = checkForUnsavedChangesAndClose
            return !isFullyClosed ? <ChartSettings onClose={checkForUnsavedChangesAndClose} /> : null
          }}
        </PreventClosingUnsavedChanges>
      ) : (
        <Delayed>
          <div className="flex h-full w-full items-center justify-center">
            <Spinner width={24} />
          </div>
        </Delayed>
      )}
    </Popover>
  )
}

interface Props {
  isPremium: boolean
  page: string
  placement?: PopoverPlacement
}

function ForexFuturesCryptoSettingsRaw({ isPremium, page }: Props) {
  const { chartLayoutModel } = useChartLayoutGlobalModelAsync()
  const settingsPopoverState = usePopoverState({ placement: 'bottom-start' })
  const onSettingsClick = useOnSettingsClick({ isPremium, page, openSettingsCallback: settingsPopoverState.show })

  return (
    <>
      <Button
        appearance="square"
        className="-mr-1 text-gray-300 hover:text-gray-400"
        focusRing={false}
        data-testid="chart-settings-open-btn"
        size="small"
        theme="transparent"
        disabled={!chartLayoutModel}
        as={PopoverTrigger}
        state={settingsPopoverState}
        onClick={onSettingsClick}
      >
        <Icon name="settingsWheel" width={12} />
      </Button>
      <ScrollableChartSettingsPopover state={settingsPopoverState} page={page} />
    </>
  )
}

// charts bundle may not be fetched and therefore withSuspense is undefined but on those pages components in this file are not used at all
export const ForexFuturesCryptoSettings = withSuspense?.(ForexFuturesCryptoSettingsRaw)
